import api from './api';
import { LoggerService } from './LoggerService';

// Core interfaces for Regulatory Intelligence
export interface RegulatoryIntelligence {
  id: string;
  document_id: string;
  analysis_type: 'compliance_impact' | 'risk_assessment' | 'deadline_extraction' | 'regulatory_change';
  ai_provider: string;
  model_used: string;
  confidence_score: number;
  analysis_results: {
    summary: string;
    key_findings: string[];
    compliance_implications: ComplianceImplication[];
    risk_factors: RiskFactor[];
    action_items: ActionItem[];
    timeline_predictions: TimelinePrediction[];
  };
  processed_at: string;
  expires_at: string;
  metadata: {
    document_type: string;
    jurisdiction: string;
    regulatory_body: string;
    fund_types_affected: string[];
  };
}

export interface ComplianceImplication {
  id: string;
  category: 'mandatory' | 'recommended' | 'guidance';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  affected_regulations: string[];
  compliance_deadline?: string;
  estimated_cost?: number;
  implementation_complexity: 'low' | 'medium' | 'high';
  fund_types_affected: string[];
}

export interface RiskFactor {
  id: string;
  risk_type: 'operational' | 'compliance' | 'reputational' | 'financial';
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  likelihood: number; // 0-1
  impact: number; // 0-1
  mitigation_strategies: string[];
  monitoring_frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly';
}

export interface ActionItem {
  id: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  title: string;
  description: string;
  deadline?: string;
  estimated_effort_hours: number;
  responsible_department: string;
  dependencies: string[];
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
}

export interface TimelinePrediction {
  id: string;
  prediction_type: 'regulatory_deadline' | 'implementation_timeline' | 'review_cycle';
  predicted_date: string;
  confidence_level: number; // 0-1
  description: string;
  factors_considered: string[];
  potential_delays: PotentialDelay[];
}

export interface PotentialDelay {
  factor: string;
  delay_probability: number; // 0-1
  estimated_delay_days: number;
  mitigation_actions: string[];
}

export interface PredictiveCompliance {
  fund_id: string;
  compliance_score: number; // 0-100
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  predicted_issues: ComplianceIssue[];
  recommendations: ComplianceRecommendation[];
  next_assessment_date: string;
  trends: ComplianceTrend[];
}

export interface ComplianceIssue {
  id: string;
  category: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  probability: number; // 0-1
  title: string;
  description: string;
  potential_impact: string;
  early_warning_indicators: string[];
  prevention_strategies: string[];
}

export interface ComplianceRecommendation {
  id: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: 'process_improvement' | 'system_upgrade' | 'training' | 'documentation';
  title: string;
  description: string;
  expected_benefit: string;
  implementation_cost: number;
  implementation_timeline: string;
  success_metrics: string[];
}

export interface ComplianceTrend {
  metric: string;
  direction: 'improving' | 'declining' | 'stable';
  change_percentage: number;
  time_period: string;
  contributing_factors: string[];
}

export interface DocumentProcessingResult {
  document_id: string;
  processing_status: 'completed' | 'failed' | 'in_progress';
  extracted_entities: ExtractedEntity[];
  key_sections: DocumentSection[];
  regulatory_references: RegulatoryReference[];
  ai_summary: string;
  confidence_score: number;
  processing_time_ms: number;
  errors?: string[];
}

export interface ExtractedEntity {
  type: 'date' | 'regulation' | 'fund_type' | 'jurisdiction' | 'penalty' | 'deadline';
  value: string;
  confidence: number;
  context: string;
  position: {
    start: number;
    end: number;
  };
}

export interface DocumentSection {
  title: string;
  content: string;
  importance: 'low' | 'medium' | 'high' | 'critical';
  page_number?: number;
  section_type: 'summary' | 'requirements' | 'penalties' | 'timeline' | 'definitions';
}

export interface RegulatoryReference {
  regulation_id: string;
  regulation_name: string;
  article_number?: string;
  amendment_type?: 'new' | 'modified' | 'repealed';
  cross_references: string[];
}

export interface RegulatoryCalendarEvent {
  id: string;
  title: string;
  description: string;
  event_type: 'deadline' | 'consultation' | 'implementation' | 'review';
  date: string;
  end_date?: string;
  jurisdiction: string;
  regulatory_body: string;
  affected_regulations: string[];
  fund_types_affected: string[];
  priority: 'low' | 'medium' | 'high' | 'critical';
  preparation_recommendations: PreparationRecommendation[];
  ai_generated: boolean;
  confidence_score?: number;
  source_document?: string;
}

export interface PreparationRecommendation {
  id: string;
  recommendation: string;
  estimated_effort: string;
  deadline_before_event: string;
  responsible_team: string;
  deliverables: string[];
  resources_needed: string[];
}

class RegulatoryIntelligenceService {
  // Document Analysis
  async analyzeRegulatoryDocument(
    documentId: string,
    analysisType: 'compliance_impact' | 'risk_assessment' | 'deadline_extraction' | 'regulatory_change',
    options: {
      aiProvider?: string;
      confidenceThreshold?: number;
      jurisdiction?: string;
      fundTypes?: string[];
    } = {}
  ): Promise<RegulatoryIntelligence> {
    try {
      const response = await api.post('/regulatory-intelligence/analyze', {
        document_id: documentId,
        analysis_type: analysisType,
        options
      });
      return response.data;
    } catch (error) {
      LoggerService.error('Error analyzing regulatory document:', error);
      throw error;
    }
  }

  async processDocumentWithAI(
    documentId: string,
    processingOptions: {
      extractEntities?: boolean;
      generateSummary?: boolean;
      identifyReferences?: boolean;
      confidenceThreshold?: number;
    } = {}
  ): Promise<DocumentProcessingResult> {
    try {
      const response = await api.post('/regulatory-intelligence/process-document', {
        document_id: documentId,
        options: processingOptions
      });
      return response.data;
    } catch (error) {
      LoggerService.error('Error processing document with AI:', error);
      throw error;
    }
  }

  // Predictive Compliance
  async getPredictiveCompliance(
    fundId: string,
    timeframe: '3_months' | '6_months' | '12_months' = '6_months'
  ): Promise<PredictiveCompliance> {
    try {
      const response = await api.get(`/regulatory-intelligence/predictive-compliance/${fundId}`, {
        params: { timeframe }
      });
      return response.data;
    } catch (error) {
      LoggerService.error('Error getting predictive compliance:', error);
      throw error;
    }
  }

  async generateComplianceReport(
    fundId: string,
    reportType: 'risk_assessment' | 'compliance_status' | 'predictive_analysis',
    options: {
      includeRecommendations?: boolean;
      includeTimeline?: boolean;
      format?: 'pdf' | 'html' | 'json';
    } = {}
  ): Promise<{ reportId: string; downloadUrl?: string; content?: any }> {
    try {
      const response = await api.post('/regulatory-intelligence/generate-report', {
        fund_id: fundId,
        report_type: reportType,
        options
      });
      return response.data;
    } catch (error) {
      LoggerService.error('Error generating compliance report:', error);
      throw error;
    }
  }

  // Regulatory Calendar
  async getRegulatoryCalendar(
    startDate: string,
    endDate: string,
    filters: {
      jurisdiction?: string[];
      fundTypes?: string[];
      priority?: string[];
      eventTypes?: string[];
    } = {}
  ): Promise<RegulatoryCalendarEvent[]> {
    const response = await api.get('/regulatory-calendar/events', {
      params: {
        start_date: startDate,
        end_date: endDate,
        jurisdictions: filters.jurisdiction,
        importance_levels: filters.priority,
        event_types: filters.eventTypes
      }
    });
    return response.data;
  }

  async generateCalendarRecommendations(
    eventId: string,
    fundProfile: {
      fundType: string;
      jurisdiction: string;
      aum: number;
      complexityLevel: 'low' | 'medium' | 'high';
    }
  ): Promise<PreparationRecommendation[]> {
    try {
      const response = await api.post(`/regulatory-calendar/events/${eventId}/ai-recommendations`, {
        fund_profile: fundProfile
      });
      return response.data;
    } catch (error) {
      LoggerService.error('Error generating calendar recommendations:', error);
      throw error;
    }
  }

  // AI Model Management
  async getAvailableModels(): Promise<{ [providerId: string]: string[] }> {
    try {
      const response = await api.get('/ai-ml/models');
      return response.data;
    } catch (error) {
      LoggerService.error('Error fetching available models:', error);
      throw error;
    }
  }

  async getModelPerformanceMetrics(
    modelId: string,
    timeframe: '24h' | '7d' | '30d' = '7d'
  ): Promise<{
    accuracy: number;
    avgResponseTime: number;
    costPerRequest: number;
    successRate: number;
    totalRequests: number;
  }> {
    try {
      const response = await api.get(`/ai-ml/models/${modelId}/metrics`, {
        params: { timeframe }
      });
      return response.data;
    } catch (error) {
      LoggerService.error('Error fetching model metrics:', error);
      throw error;
    }
  }

}

export const regulatoryIntelligenceService = new RegulatoryIntelligenceService();